import React from "react";
import { Link } from "react-router-dom";
import useReveal from "../hooks/useReveal";
import PageHero from "../components/PageHero";
import StatsLine from "../components/StatsLine";
import TestimonialsPremium from "../components/TestimonialsPremium";
import CtaPremium from "../components/CtaPremium";
import Logos from "../components/Logos";
import { CREDENTIALS, AREAS, PROCESS, PRINCIPLES } from "../mock";
import { CheckCircle2, ArrowRight } from "lucide-react";

const VALUES = [
  { n: "01", t: "Precision", d: "Every detail is drawn, costed and resolved before site work begins. We measure twice and cut once." },
  { n: "02", t: "Reliability", d: "Fixed-price proposals, weekly progress reports and a single accountable partner from start to finish." },
  { n: "03", t: "Expertise", d: "Time-served craftsmen, certified trades and decades of combined experience on every project." },
  { n: "04", t: "Aftercare", d: "A 24-month aftercare promise and clear warranty documentation on every completed build." },
];

export default function AboutPage() {
  useReveal();
  return (
    <div className="page-enter">
      <PageHero
        variant="dark"
        eyebrow="About — RS Construction"
        title="Bespoke construction"
        italic="for the discerning client."
        subtitle="From project planning to completion, we deliver comprehensive construction solutions with precision, reliability and professional expertise. Your trusted partner for every build."
        image="https://images.unsplash.com/photo-1503387762-592deb58ef4e?crop=entropy&cs=srgb&fm=jpg&q=90&w=2400"
        meta={[
          { label: "Outstanding", value: "Service" },
          { label: "Exclusive", value: "Pricing" },
          { label: "Industry", value: "Expertise" },
          { label: "Trusted", value: "Quality" },
        ]}
      />

      {/* Manifesto statement */}
      <section className="bg-bone py-24 lg:py-40">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-3 rv">
            <div className="text-eyebrow text-amber mb-5">— Our promise</div>
          </div>
          <div className="lg:col-span-9 rv">
            <h2 className="font-display-tight text-4xl md:text-6xl lg:text-[5rem] leading-[1.05] text-ink text-balance">
              We don&apos;t just provide tools — we deliver <span className="italic font-display text-amber">complete solutions</span> to help you plan, build, improve and finish your projects with <span className="italic font-display">confidence.</span>
            </h2>
          </div>
        </div>
      </section>

      {/* Image strip */}
      <section className="grid grid-cols-1 md:grid-cols-3 gap-1 bg-ink">
        {[
          "https://images.unsplash.com/photo-1503387762-592deb58ef4e?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
          "https://images.unsplash.com/photo-1581094794329-c8112a89af12?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
          "https://images.unsplash.com/photo-1600210492493-0946911123ea?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
        ].map((src, i) => (
          <div key={i} className="relative overflow-hidden aspect-[3/4] md:aspect-[4/5] rv" style={{ transitionDelay: `${i * 100}ms` }}>
            <img src={src} alt="" className="w-full h-full object-cover img-zoom" loading="lazy" />
          </div>
        ))}
      </section>

      {/* Story */}
      <section className="bg-bone py-24 lg:py-36">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-10 lg:gap-20">
          <div className="lg:col-span-5 rv">
            <div className="text-eyebrow text-amber mb-5">— Our story</div>
            <h2 className="font-display-tight text-5xl lg:text-7xl leading-[0.95] text-ink text-balance">
              Built on craft.<br /><span className="italic font-display">Grown on trust.</span>
            </h2>
          </div>
          <div className="lg:col-span-7 space-y-5 text-ink-soft text-lg leading-relaxed rv">
            <p>RS Construction (HI) Ltd is a UK-based construction company offering a complete service to homeowners, developers and trade professionals. We pair an in-house team of experienced builders with a curated supply of professional-grade tools and materials — so every project gets the right people and the right kit from day one.</p>
            <p>Our work spans seven core disciplines — design and planning, installation, energy-improvement retrofits, finance and payment support, tool and plant hire, waste disposal, and specialist interior finishing. A single project manager owns your build end-to-end, with one accountable point of contact for every decision.</p>
            <p>We measure success by repeat business and personal recommendations — and we have built our reputation, project by project, on outstanding service, exclusive pricing and industry expertise.</p>
          </div>
        </div>
      </section>

      <StatsLine />

      {/* Three principles */}
      <section className="bg-bone-2 py-24 lg:py-36">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-10">
          <div className="grid lg:grid-cols-12 gap-10 mb-16">
            <div className="lg:col-span-6 rv">
              <div className="text-eyebrow text-amber mb-5">— Three principles</div>
              <h2 className="font-display-tight text-5xl lg:text-7xl leading-[0.95] text-ink text-balance">
                Every project,<br /><span className="italic font-display">three guarantees.</span>
              </h2>
            </div>
          </div>
          <div className="grid md:grid-cols-3 gap-[1px] bg-line">
            {PRINCIPLES.map((p, i) => (
              <div key={p.t} className="bg-bone p-10 lg:p-14 rv" style={{ transitionDelay: `${i * 80}ms` }}>
                <div className="font-display-tight text-amber text-5xl mb-6">0{i + 1}</div>
                <h3 className="font-display-tight text-3xl text-ink mb-3">{p.t}</h3>
                <p className="text-ink-soft leading-relaxed">{p.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="bg-bone py-24 lg:py-36">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-10">
          <div className="grid lg:grid-cols-12 gap-10 mb-16">
            <div className="lg:col-span-6 rv">
              <div className="text-eyebrow text-amber mb-5">— What we stand for</div>
              <h2 className="font-display-tight text-5xl lg:text-7xl leading-[0.95] text-ink text-balance">
                Four values<br /><span className="italic font-display">we never compromise.</span>
              </h2>
            </div>
          </div>
          <div className="grid md:grid-cols-2 gap-[1px] bg-line">
            {VALUES.map((v, i) => (
              <div key={v.n} className="bg-bone p-10 lg:p-14 rv" style={{ transitionDelay: `${i * 80}ms` }}>
                <div className="flex items-start justify-between mb-8">
                  <div className="text-eyebrow text-amber">{v.n}</div>
                  <div className="font-display text-4xl text-ink/10">{v.n}</div>
                </div>
                <h3 className="font-display-tight text-4xl text-ink mb-4">{v.t}</h3>
                <p className="text-ink-soft leading-relaxed text-[15px]">{v.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="bg-ink text-bone py-24 lg:py-36">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-16">
          <div className="lg:col-span-5 rv">
            <div className="text-eyebrow text-amber mb-5">— How we work</div>
            <h2 className="font-display-tight text-5xl lg:text-7xl leading-[0.95] text-bone text-balance">
              From planning<br /><span className="italic font-display text-amber">to completion.</span>
            </h2>
          </div>
          <div className="lg:col-span-7">
            {PROCESS.map((p, i) => (
              <div key={i} className="grid grid-cols-[64px_1fr] md:grid-cols-[100px_1fr] gap-8 py-8 border-t border-line-light last:border-b rv" style={{ transitionDelay: `${i * 80}ms` }}>
                <div className="font-display-tight text-4xl md:text-6xl text-amber leading-none">{p.step}</div>
                <div>
                  <h3 className="font-display text-2xl md:text-3xl text-bone mb-2">{p.title}</h3>
                  <p className="text-bone/70 text-[15px] leading-relaxed">{p.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Credentials + areas */}
      <section className="bg-bone py-20">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-10 grid lg:grid-cols-2 gap-12">
          <div className="rv">
            <div className="text-eyebrow text-amber mb-5">— Accreditations & guarantees</div>
            <div className="grid grid-cols-2 gap-3">
              {CREDENTIALS.map((c) => (
                <div key={c} className="border border-line p-4 bg-bone-2/40 flex items-center gap-3"><CheckCircle2 className="w-4 h-4 text-amber" /><span className="text-ink font-medium">{c}</span></div>
              ))}
            </div>
          </div>
          <div className="rv">
            <div className="text-eyebrow text-amber mb-5">— Areas covered</div>
            <div className="flex flex-wrap gap-2">
              {AREAS.map((a) => <span key={a} className="inline-block border border-line px-3.5 py-1.5 text-sm">{a}</span>)}
            </div>
            <Link to="/contact" className="inline-flex items-center gap-3 mt-8 text-eyebrow text-ink link-line">
              Enquire about your project <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      <Logos />
      <TestimonialsPremium />
      <CtaPremium />
    </div>
  );
}
